import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useActiveCustomerPermissions } from '@/hooks/useActiveCustomerPermissions';
import { CustomerDashboardSection } from '@/types/permissions';
import { Loader2 } from 'lucide-react';

interface ProtectedCustomerRouteProps {
  children: ReactNode;
  requiredPermission?: CustomerDashboardSection;
}

export function ProtectedCustomerRoute({ 
  children, 
  requiredPermission 
}: ProtectedCustomerRouteProps) {
  const { currentUser, userData, loading: authLoading, hasRole } = useAuth();
  const { hasSectionAccess, loading: permissionsLoading } = useActiveCustomerPermissions();
  const location = useLocation();

  // Show loading while auth or permissions are loading
  if (authLoading || permissionsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Check for both Firebase Auth (currentUser) and OTP-based auth (userData)
  const isAuthenticated = currentUser || userData;

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to unauthorized if not a customer
  if (!hasRole('customer')) {
    return <Navigate to="/unauthorized" replace />;
  }

  // If a specific permission is required, check it
  if (requiredPermission && !hasSectionAccess(requiredPermission)) {
    // Redirect to customer dashboard if user doesn't have access to this section
    return <Navigate to="/dashboard/customer" replace />;
  }

  return <>{children}</>;
}